import classNames from "classnames";
import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { useGetClientOverviewQuery } from "../../../slices/ReportDataSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { FilterMenu } from "../../works/board/FilterMenu";
import { askTable } from "../ask/reportAsk.utils";
import {
  clientsOfPerson,
  clientsWithWork,
  OwnerMode,
  ownerRows,
  personOf,
} from "../moreReports.utils";
import { formatMoney } from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { useViewExtra } from "../saved/SavedViewContext";
import {
  Avatar,
  Card,
  Empty,
  LINK,
  LoadError,
  Loading,
  Note,
  NUM,
  ReportPage,
  Segmented,
  TableBox,
  TD,
  TH,
  useFocusRows,
} from "../ui/ReportUi";
import { useAllInvoices, useAllWorks } from "../useReportData";

const MODES: { value: OwnerMode; label: string }[] = [
  { value: "owner", label: "By client owner" },
  { value: "manager", label: "By client manager" },
];
const TABLE = "Clients per person";

/** Clients by owner / manager (2026-11-05): each person's clients, open and overdue work, unpaid balance. */
export const ByOwnerReport: FC = () => {
  const access = useReportsAccess();
  const orgId = getLinkedFirm()?.orgId || "";
  const [mode, setMode] = useViewExtra<OwnerMode>("by", "owner", MODES, "");
  const [people, setPeople] = useState<string[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const overview = useGetClientOverviewQuery({ orgId }, { skip: !orgId });
  const works = useAllWorks({
    hideGreenStatus: true,
    userIds: scopeUserIds(access, []),
  });
  const invoices = useAllInvoices({}, !access.billing);
  const isLoading =
    overview.isLoading ||
    works.isLoading ||
    (access.billing && invoices.isLoading);
  const clients = useMemo(
    () =>
      clientsWithWork(
        overview.data?.clients ?? [],
        works.data,
        access.billing ? invoices.data : [],
      ),
    [overview.data, works.data, invoices.data, access.billing],
  );
  const allRows = useMemo(() => ownerRows(clients, mode), [clients, mode]);
  const rows = useMemo(
    () =>
      people.length
        ? allRows.filter((r) => people.includes(r.id || "none"))
        : allRows,
    [allRows, people],
  );
  const selected = rows.find((r) => r.id === picked) ?? rows[0];
  const theirClients = useMemo(
    () => (selected ? clientsOfPerson(clients, mode, selected.id) : []),
    [clients, mode, selected],
  );
  const other: OwnerMode = mode === "owner" ? "manager" : "owner";
  const role = mode === "owner" ? "Client owner" : "Client manager";
  const otherRole = other === "owner" ? "Client owner" : "Client manager";

  const shown = useFocusRows(TABLE, rows, (r) => r.id || "none");
  const { pageRows, pager } = usePagedRows(shown);
  const clientPage = usePagedRows(theirClients);

  const onExport = () =>
    exportDataToExcel(
      clients.map((c) => ({
        Client: c.name,
        "Client owner": c.owner?.name || "Not set",
        "Client manager": c.manager?.name || "Not set",
        "Open work": c.open,
        Overdue: c.overdue,
        ...(access.billing
          ? { "Unpaid balance": Math.round(c.balance * 100) / 100 }
          : {}),
      })),
      "Clients by owner and manager",
    );

  const ask = useMemo(
    () => ({
      title: "Clients by owner / manager",
      path: "/reports/by-owner",
      description: `Each ${role.toLowerCase()}'s clients with their open work, overdue items${
        access.billing ? " and unpaid balance" : ""
      }.`,
      kpis: [],
      tables: [
        askTable(
          TABLE,
          rows,
          [
            { key: "person", label: role, kind: "text", value: (r) => r.name },
            {
              key: "clients",
              label: "Clients",
              kind: "number",
              value: (r) => r.clients,
            },
            {
              key: "open",
              label: "Open work",
              kind: "number",
              value: (r) => r.open,
            },
            {
              key: "overdue",
              label: "Overdue",
              kind: "number",
              value: (r) => r.overdue,
            },
            ...(access.billing
              ? [
                  {
                    key: "balance",
                    label: "Unpaid balance",
                    kind: "money" as const,
                    value: (r: typeof rows[number]) => r.balance,
                  },
                ]
              : []),
          ],
          (r) => r.id || "none",
        ),
        askTable(
          "Clients",
          clients,
          [
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (c) => c.name,
            },
            {
              key: "owner",
              label: "Client owner",
              kind: "text",
              value: (c) => c.owner?.name || "Not set",
            },
            {
              key: "manager",
              label: "Client manager",
              kind: "text",
              value: (c) => c.manager?.name || "Not set",
            },
            {
              key: "open",
              label: "Open work",
              kind: "number",
              value: (c) => c.open,
            },
            {
              key: "overdue",
              label: "Overdue",
              kind: "number",
              value: (c) => c.overdue,
            },
          ],
          (c) => c.customerId,
          (c) => c.link,
        ),
      ],
    }),
    [rows, clients, role, access.billing],
  );

  const personOptions = allRows.map((r) => ({
    value: r.id || "none",
    label: r.name,
  }));

  return (
    <ReportPage
      title={"Clients by owner / manager"}
      description={
        "Each person's clients with their open work, overdue items and unpaid balance. Balances need billing access."
      }
      onExport={clients.length ? onExport : undefined}
      ask={isLoading ? undefined : ask}
      askSuggestions={[
        "Who has the most overdue work?",
        "Which clients have no owner?",
        "Who has the biggest unpaid balance?",
      ]}
      filters={
        <>
          <Segmented
            label={"Group clients"}
            options={MODES}
            value={mode}
            onChange={(m) => {
              setMode(m);
              setPicked(null);
            }}
          />
          <FilterMenu
            placeholder={"Person"}
            options={personOptions}
            selected={people}
            onChange={setPeople}
          />
        </>
      }>
      {(overview.isError || works.isError || invoices.isError) && <LoadError />}
      {overview.data && !overview.data.teamAvailable && (
        <Note>
          Client owners and managers need a database update first. Until then
          every client shows under &quot;No client owner&quot;.
        </Note>
      )}

      <Card title={role}>
        {isLoading ? (
          <Loading />
        ) : !rows.length ? (
          <Empty text={"No clients to show."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  {role}
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Clients
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Open work
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Overdue
                </th>
                {access.billing && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Unpaid balance
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => (
                <tr
                  key={r.id || "none"}
                  className={classNames(
                    "cursor-pointer hover:bg-[#FBFAFF]",
                    selected?.id === r.id && "bg-[#F6F3FF]",
                  )}
                  onClick={() => setPicked(r.id)}>
                  <td className={`${TD} font-bold`}>
                    <button
                      type={"button"}
                      className={"inline-flex items-center text-left font-bold"}
                      aria-pressed={selected?.id === r.id}>
                      {r.id ? (
                        <Avatar name={r.name} color={r.color || undefined} />
                      ) : (
                        <Avatar name={"—"} color={"#8A90A2"} />
                      )}
                      {r.name}
                    </button>
                  </td>
                  <td className={`${TD} ${NUM}`}>{r.clients}</td>
                  <td className={`${TD} ${NUM}`}>{r.open}</td>
                  <td
                    className={`${TD} ${NUM} font-bold ${
                      r.overdue ? "text-[#B42318]" : ""
                    }`}>
                    {r.overdue}
                  </td>
                  {access.billing && (
                    <td className={`${TD} ${NUM}`}>{formatMoney(r.balance)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>

      {selected && !isLoading && (
        <Card
          title={
            selected.id
              ? `${selected.name}'s clients`
              : `Clients with no ${role.toLowerCase()}`
          }>
          <TableBox pager={clientPage.pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  {otherRole}
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Open work
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Overdue
                </th>
                {access.billing && (
                  <th scope={"col"} className={`${TH} ${NUM}`}>
                    Unpaid
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {clientPage.pageRows.map((c) => {
                const p = personOf(c, other);
                return (
                  <tr key={c.customerId}>
                    <td className={TD}>
                      {c.link ? (
                        <Link to={c.link} className={LINK}>
                          {c.name}
                        </Link>
                      ) : (
                        <span className={"font-bold"}>{c.name}</span>
                      )}
                    </td>
                    <td className={`${TD} ${p ? "" : "text-[#8A90A2]"}`}>
                      {p?.name || "Not set"}
                    </td>
                    <td className={`${TD} ${NUM}`}>{c.open}</td>
                    <td
                      className={`${TD} ${NUM} font-bold ${
                        c.overdue ? "text-[#B42318]" : ""
                      }`}>
                      {c.overdue}
                    </td>
                    {access.billing && (
                      <td className={`${TD} ${NUM}`}>
                        {formatMoney(c.balance)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </TableBox>
        </Card>
      )}

      <Note>
        Click a person to see their clients. Set a client&apos;s owner and
        manager on the client&apos;s page (Client team) or for many clients at
        once in Contacts.
        {!access.firmWide && " You see your own clients and work."}
      </Note>
    </ReportPage>
  );
};
